import {store} from '@/store';
import {userAtom} from '@/store/user';
import {buildMbApiUrl, fetchWithTimeout} from '@/util/request-utils';
import {uploadPresignedPostBase64} from '@/lib/presigned-upload';
import {
    type JobResponse,
    type LifestyleParams,
    type LifestylePromptResponse,
    type PresignedUrlResponse,
} from '@/types/product-media';
import {MODELBANK_META} from './constants';

// Re-export types for convenience
export type {
    LifestyleParams,
    LifestylePromptResponse,
} from '@/types/product-media';

export async function uploadImageWithPresignedUrl(
    imageBase64: string
): Promise<string> {
    const user = store.get(userAtom);
    if (!user?.auth_token) {
        throw new Error('User not authenticated');
    }

    const meta = MODELBANK_META.get('lifestyle');
    if (!meta?.endpoints.getPresignedUrl) {
        throw new Error('Presigned URL endpoint not configured');
    }

    const url = buildMbApiUrl(`/${meta.endpoints.getPresignedUrl}`, {
        auth_token: user.auth_token,
    });

    const response = await fetchWithTimeout(url, {method: 'POST'});
    if (!response.ok) {
        throw new Error(`Failed to get presigned URL: ${response.status}`);
    }
    const presignedData: PresignedUrlResponse = await response.json();

    return uploadPresignedPostBase64(presignedData, imageBase64);
}

export async function createLifestyle(
    params: LifestyleParams = {}
): Promise<LifestylePromptResponse | null> {
    const meta = MODELBANK_META.get('lifestyle');
    const {mode = 'default', image_base64, ...restParams} = params;

    if (!image_base64) {
        throw new Error(
            'Image data (image_base64) is required for lifestyle generation.'
        );
    }

    // Only 'default' mode is currently supported
    // Style transfer mode would require backend endpoint support
    if (mode !== 'default') {
        throw new Error(
            `Lifestyle mode '${mode}' is not supported. Only 'default' mode is available.`
        );
    }

    const imageUrl = await uploadImageWithPresignedUrl(image_base64);
    const endpointPath = meta?.endpoints.generateLifestyle;

    // Guard against missing endpoint configuration
    if (!endpointPath) {
        throw new Error(
            'Lifestyle generation endpoint is not configured. Check MODELBANK_META.'
        );
    }

    const user = store.get(userAtom);
    if (!user?.auth_token) {
        throw new Error('User not authenticated');
    }

    // Backend expects 'images' array for generate_banana endpoint
    const payload: Record<string, unknown> = {
        images: [imageUrl],
        prompt: restParams.positive_prompt ?? '',
    };

    // Add optional parameters for better quality and format control
    if (restParams.negative_prompt) {
        payload.negative_prompt = restParams.negative_prompt;
    }
    if (restParams.aspect_ratio) {
        payload.aspect_ratio = restParams.aspect_ratio;
    }
    if (restParams.width) {
        payload.width = restParams.width;
    }
    if (restParams.height) {
        payload.height = restParams.height;
    }
    if (restParams.guidance_scale) {
        payload.guidance_scale = restParams.guidance_scale;
    }
    if (restParams.num_inference_steps) {
        payload.num_inference_steps = restParams.num_inference_steps;
    }
    if (restParams.image_strength !== undefined) {
        payload.image_strength = restParams.image_strength;
    }
    if (restParams.controlnet_conditioning_scale !== undefined) {
        payload.controlnet_conditioning_scale =
            restParams.controlnet_conditioning_scale;
    }

    const url = buildMbApiUrl(`/${endpointPath}`, {
        auth_token: user.auth_token,
    });

    const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Failed to create lifestyle: ${response.status}`);
    }

    return response.json();
}

export async function getLifestyleResults(params: {
    request_id: string;
}): Promise<{images: string[]; finished: boolean} | null> {
    const user = store.get(userAtom);
    if (!user?.auth_token) {
        return null;
    }

    const meta = MODELBANK_META.get('lifestyle');
    const {request_id} = params;
    const endpointPath = meta?.endpoints.indexLifestyle;
    if (!endpointPath) {
        return null;
    }

    const url = buildMbApiUrl(
        `/${endpointPath}`,
        {auth_token: user.auth_token},
        {request_id, type: 'generate_banana'}
    );

    const response = await fetchWithTimeout(url);
    if (!response.ok) {
        return null;
    }

    return response.json();
}

export async function saveLifestyleImageToMedia(
    productId: string,
    imageUrl: string,
    options?: {
        visible?: boolean;
        format?: string;
    }
): Promise<JobResponse | null> {
    const user = store.get(userAtom);
    if (!user?.auth_token) {
        throw new Error('User not authenticated');
    }

    const meta = MODELBANK_META.get('lifestyle');
    if (!meta?.endpoints.create) {
        throw new Error('Create endpoint not configured');
    }

    // Server expects asset_id as number
    const numericProductId = parseInt(productId, 10);
    if (!Number.isFinite(numericProductId)) {
        throw new Error(`Invalid product ID: ${productId}`);
    }
    const payload = {
        job: {
            media_kind: meta.mediaKind,
            asset_type: 'Product',
            asset_id: numericProductId,
            settings: {
                image_url: imageUrl,
                width: 2048,
                height: 2048,
                visible_in_library: options?.visible ?? false,
                format: options?.format,
            },
        },
    };

    const url = buildMbApiUrl(`/${meta.endpoints.create}`, {
        auth_token: user.auth_token,
    });

    const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to save lifestyle image to media: ${response.status}`
        );
    }

    return response.json();
}

export interface RemoveProductResponse {
    request_id?: string;
    error_message?: string;
}

export async function removeProductFromLifestyleImage(
    imageUrl: string,
    maskUrl: string
): Promise<RemoveProductResponse | null> {
    const user = store.get(userAtom);
    if (!user?.auth_token) {
        return {error_message: 'User not authenticated'};
    }

    const endpointPath = 'api/v1/ai_lifestyles/remove_product.json';

    try {
        const url = buildMbApiUrl(`/${endpointPath}`, {
            auth_token: user.auth_token,
        });

        const response = await fetchWithTimeout(url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({image_url: imageUrl, mask_url: maskUrl}),
        });

        if (!response.ok) {
            return {
                error_message: `Failed to remove product: ${response.status}`,
            };
        }

        return response.json();
    } catch (error) {
        if (error instanceof Error) return {error_message: error.message};

        return {
            error_message: 'Unknown error during product removal initiation.',
        };
    }
}

export interface RemoveProductPollingResponse {
    finished: boolean;
    images?: string[];
    error_message?: string;
}

export async function getRemoveProductPollingResult(
    requestId: string
): Promise<RemoveProductPollingResponse | null> {
    const user = store.get(userAtom);
    if (!user?.auth_token) {
        return {finished: false, error_message: 'User not authenticated'};
    }

    const endpointPath = 'api/v1/ai_lifestyles/results.json';

    try {
        const url = buildMbApiUrl(
            `/${endpointPath}`,
            {auth_token: user.auth_token},
            {request_id: requestId, type: 'remove_product'}
        );

        const response = await fetchWithTimeout(url);
        if (!response.ok) {
            return {
                finished: false,
                error_message: `Failed: ${response.status}`,
            };
        }

        const data: RemoveProductPollingResponse | null = await response.json();
        if (!data) return {finished: false};

        return data;
    } catch (error) {
        return {
            finished: false,
            error_message:
                error instanceof Error
                    ? error.message
                    : 'Polling for remove product status failed',
        };
    }
}
